import {
  StyleSheet,
  Text,
  View,
  BackHandler,
  StatusBar,
  Share,
  Platform,
} from 'react-native';
import { AppAlert } from '../services/AppAlert';
import React, { useCallback, useRef, useState } from 'react';
import { theme } from '../theme';
import WebView from 'react-native-webview';
import RNBlobUtil from 'react-native-blob-util';
import { PermissionsAndroid } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { LANDING_PAGE_URL, WEB_LOGIN_URL } from '../CONSTANTS';
import { fs, v, normalizeEmail } from '../utils/libs';
import { getBackofficeCredentials } from '../services/SecureCredentials';
import { useAppDispatch } from '../redux/helper';
import { setPreventsBiomtericsPrompt } from '../redux/uiSlice';

const Browser = ({ navigation }: { navigation: any }) => {
  const webViewRef = useRef<WebView | null>(null);
  const [progress, setProgress] = useState(0);
  const [canGoBack, setCanGoBack] = useState(false);
  const [url, setUrl] = useState(WEB_LOGIN_URL);
  const dispatch = useAppDispatch();

  const handleExit = () => {
    AppAlert.alert(
      'BizPlus Backoffice',
      'Are you sure you want to exit your BizPlus back-office?',
      [
        { text: 'Cancel', onPress: () => null, style: 'cancel' },
        {
          text: 'Exit',
          onPress: () => {
            navigation.goBack();
            StatusBar.setBackgroundColor(theme.colors.primaryDark);
          },
        },
      ],
    );
  };

  const goBack = useCallback(() => {
    if (url === WEB_LOGIN_URL) {
      handleExit();
    } else if (canGoBack) {
      webViewRef.current?.goBack();
    } else {
      handleExit();
    }
  }, [url, canGoBack]);

  const handleBackPress = useCallback(() => {
    goBack();
    return true;
  }, [goBack]);

  useFocusEffect(
    useCallback(() => {
      const backHandler = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        backHandler.remove();
      };
    }, [handleBackPress]),
  );

  const redirectTo = (newURL: string) => {
    console.log('newURL', newURL);
    if (newURL === LANDING_PAGE_URL) {
      const redirectTo = 'window.location = "' + newURL + '"';
      webViewRef.current?.injectJavaScript(redirectTo);
    }
  };

  const injectLoginScript = async () => {
    try {
      const creds = await getBackofficeCredentials();
      if (!creds?.email || !creds?.password) {
        console.log('No credentials found in storage');
        return;
      }

      const email = normalizeEmail(creds.email);
      const password = creds.password.trim();

      const loginScript = `
        (function() {
          function setReactInputValue(input, value) {
            // Get the React fiber node
            let fiber = input._reactProps || input._targetInst;

            // Set the value
            input.value = value;

            // Create and dispatch events
            const inputEvent = new Event('input', { bubbles: true });
            const changeEvent = new Event('change', { bubbles: true });

            // Add target property to the events
            Object.defineProperty(inputEvent, 'target', { value: input });
            Object.defineProperty(changeEvent, 'target', { value: input });

            input.dispatchEvent(inputEvent);
            input.dispatchEvent(changeEvent);

            // Update React's internal state
            if (fiber && fiber.onChange) {
              fiber.onChange({ target: input });
            }
          }

          function waitForElement(selector, callback, maxTries = 10) {
            if (maxTries <= 0) return;

            const element = document.querySelector(selector);
            if (element) {
              callback(element);
            } else {
              setTimeout(() => waitForElement(selector, callback, maxTries - 1), 500);
            }
          }

          function performLogin() {

            // Wait for email input
            waitForElement('#email', (emailInput) => {
              setReactInputValue(emailInput, '${email}');

              // Wait for password input
              waitForElement('#password', (passwordInput) => {
                setReactInputValue(passwordInput, '${password}');

                // Wait for submit button
                waitForElement('button[type="submit"]', (submitButton) => {
                  setTimeout(() => {
                    if (!submitButton.disabled) {
                      submitButton.click();
                      window.ReactNativeWebView.postMessage("login_success");
                    } else {
                      console.log('Submit button is disabled');
                    }
                  }, 16);
                });
              });
            });
          }

          // Start the login process
          performLogin();
        })();
      `;

      webViewRef.current?.injectJavaScript(loginScript);
    } catch (error) {
      console.error('Error in injectLoginScript:', error);
    } finally {
      StatusBar.setBackgroundColor(theme.colors.textGray900);
    }
  };

  return (
    <View style={{ flex: 1 }}>
      <View
        style={{
          height: v(50),
          width: '100%',
          backgroundColor: theme.colors.textGray900,
        }}
      />
      <WebView
        cacheEnabled={true}
        onLoadProgress={event => {
          setProgress(event.nativeEvent.progress);
        }}
        autoManageStatusBarEnabled={false}
        domStorageEnabled={true}
        allowFileAccessFromFileURLs
        onNavigationStateChange={event => {
          console.log('Navigation state changed:', event);
          setCanGoBack(event.canGoBack);
          setUrl(event.url);
          redirectTo(event.url);
        }}
        cacheMode="LOAD_CACHE_ELSE_NETWORK"
        javaScriptEnabled
        originWhitelist={['*']}
        ref={webViewRef}
        mixedContentMode="always"
        source={{ uri: url }}
        allowsProtectedMedia
        allowsLinkPreview
        setSupportMultipleWindows={true}
        onMessage={event => {
          if (event.nativeEvent.data === 'login_success') {
            StatusBar.setBackgroundColor(theme.colors.textGray900);
          } else {
            console.log(
              'Received message from webview:',
              event.nativeEvent.data,
            );
          }
        }}
        onError={syntheticEvent => {
          const { nativeEvent } = syntheticEvent;
          console.error('WebView error:', nativeEvent);
        }}
        onLoadEnd={syntheticEvent => {
          const { nativeEvent } = syntheticEvent;
          if (nativeEvent.url === WEB_LOGIN_URL) {
            setTimeout(() => {
              injectLoginScript();
            }, 16);
          }
        }}
        onFileDownload={async event => {
          console.log('File download event:', event);
          const downloadUrl = event.nativeEvent.downloadUrl;

          if (!downloadUrl) {
            console.log('No download URL found');
            return;
          }

          try {
            const date = new Date();
            const suggestedName =
              downloadUrl.split('/').pop()?.split('?')[0] ||
              `file_${date.getTime()}`;
            if (Platform.OS === 'android') {
              if (Platform.Version < 29) {
                dispatch(setPreventsBiomtericsPrompt(true));
                const granted = await PermissionsAndroid.request(
                  PermissionsAndroid.PERMISSIONS.WRITE_EXTERNAL_STORAGE,
                  {
                    title: 'Storage Permission',
                    message: 'App needs access to storage to download files',
                    buttonPositive: 'OK',
                  },
                );
                if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
                  return;
                }
              }
              await RNBlobUtil.config({
                addAndroidDownloads: {
                  useDownloadManager: true,
                  title: suggestedName,
                  description: 'Downloading file',
                  mime: undefined as any,
                  mediaScannable: true,
                  notification: true,
                },
              }).fetch('GET', downloadUrl);
              AppAlert.alert('Success', `Downloading ${suggestedName}...`);
            } else {
              // iOS: Download and immediately share
              const filePath = `${RNBlobUtil.fs.dirs.DocumentDir}/${suggestedName}`;
              const res = await RNBlobUtil.config({ path: filePath }).fetch(
                'GET',
                downloadUrl,
              );
              const savedPath = res.path();

              try {
                // Share the downloaded file so user can save it to Files app, iCloud, etc.
                await Share.share({
                  title: suggestedName,
                  url: `file://${savedPath}`,
                  message: `Downloaded: ${suggestedName}`,
                });
              } catch (shareError) {
                console.log('Share error, file saved to:', savedPath);
                AppAlert.alert(
                  'Downloaded',
                  `File saved to app Documents. You can access it from the Files app under "On My iPhone" > BizPlus.`,
                );
              }
            }
          } catch (error) {
            console.error('Download error:', error);
            AppAlert.alert('Error', 'Failed to download file');
          } finally {
            dispatch(setPreventsBiomtericsPrompt(false));
          }
        }}
      />
      {progress < 1 && url === WEB_LOGIN_URL && (
        <View style={styles.loadingOverlay}>
          <Text>Progress: {Math.round(progress * 100)}%</Text>
        </View>
      )}
    </View>
  );
};

export default Browser;

const styles = StyleSheet.create({
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: fs(18),
    marginBottom: v(5),
    color: theme.colors.shadow,
  },
});
